import React, { useState } from 'react';
import { Box, TextField, Button, Typography, InputAdornment, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AventiqoLoginbackground22 from "../../assets/images/AventiqoLoginbackground22.jpg";
import AventiqoSmallLogo from "../../assets/images/AventiqoSmallLogo.png";
import AventiqoLOGO from "../../assets/images/AventiqoLOGO.png";
import Loader from '../../common/spinner';
import { postData, putData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

const ForgotPasswordPage = () => {
    // Steps: 1 (Email), 2 (OTP), 3 (Reset)
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [otpInput, setOtpInput] = useState('');
    const [passwords, setPasswords] = useState({ new: '', confirm: '' });
    const [showPass, setShowPass] = useState({ new: false, confirm: false });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleError = (err) => {
        const msg = err.response?.data?.message || err.message || "An error occurred";
        setError(err.response?.status === 404 ? "User not found with this email." : msg);
    };

    const handleGenerateOtp = async () => {
        if (!email.includes('@') || !email.includes('.')) return setError("Enter a valid email.");
        setIsLoading(true); setError('');
        try {
            await postData(API_ENDPOINTS.generateVerificationOtp, { email });
            setStep(2);
        } catch (err) { handleError(err); }
        finally { setIsLoading(false); }
    };

    const handleVerifyOTP = async () => {
        setIsLoading(true); setError('');
        try {
            await postData(API_ENDPOINTS.verifyOtp, { email, otp: otpInput });
            setStep(3);
        } catch (err) { handleError(err); }
        finally { setIsLoading(false); }
    };

    const handleChangePassword = async () => {
        if (passwords.new !== passwords.confirm) return setError("Passwords do not match.");
        if (passwords.new.length < 6) return setError("Password must be at least 6 characters.");
        setIsLoading(true); setError('');
        try {
            await putData(API_ENDPOINTS.resetUserPassword, { email, newPassword: passwords.new, confirmPassword: passwords.confirm });
            navigate('/login');
        } catch (err) { handleError(err); }
        finally { setIsLoading(false); }
    };

    const inputStyle = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderRadius: '4px',
            color: '#fff',
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
            '&:hover fieldset': { borderColor: '#2563EB' },
            '&.Mui-focused fieldset': { borderColor: '#2563EB' },
        },
        '& .MuiInputBase-input::placeholder': {
            color: '#8a8f98',
            opacity: 1,
        }
    };

    const renderField = (placeholder, value, onChange, type = 'text', toggleKey = null) => (
        <TextField
            fullWidth size="small" placeholder={placeholder} type={type}
            value={value} onChange={(e) => onChange(e.target.value)}
            sx={inputStyle}
            InputProps={{
                endAdornment: toggleKey && (
                    <InputAdornment position="end">
                        <IconButton onClick={() => setShowPass(s => ({ ...s, [toggleKey]: !s[toggleKey] }))} sx={{ color: '#bbb' }}>
                            {showPass[toggleKey] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                )
            }}
        />
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                height: '100vh',
                width: '100vw',
                backgroundColor: '#0A0A0F',
                backgroundImage: {
                    xs: 'none',
                    md: `linear-gradient(to right, rgba(10,10,15,0) 0%, rgba(10,10,15,0.65) 42%, #0A0A0F 62%), url(${AventiqoLoginbackground22})`,
                },
                backgroundSize: 'contain, contain',
                backgroundPosition: 'center, left center',
                backgroundRepeat: 'no-repeat, no-repeat',
            }}
        >
            {isLoading && <Loader />}
            <Box
                sx={{
                    flex: 1,
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'start',
                    ml: { md: 6, lg: 10  }
                }}
            >
                <Box component="img" src={AventiqoSmallLogo} alt="Aventiqo" sx={{ width: 150 }} />
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '350px',
                        textAlign: 'center',
                        bgcolor: 'rgba(10, 10, 15, 0.85)',
                        border: '1px solid rgba(41, 182, 246, 0.35)',
                        borderRadius: 3,
                        boxShadow: '0 0 40px rgba(33, 150, 243, 0.15)',
                        p: 4,
                    }}
                >
                    {/* <Box component="img" src={AventiqoLOGO} alt="Aventiqo" sx={{ width: 180, maxWidth: '100%', mb: 1 }} />

                    <Box sx={{ width: '30%', mx: 'auto', borderBottom: '1px solid #EDC30099', mb: 3 }} /> */}

                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#fff' }}>Forgot Password?</Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: '#bbb' }}>
                        {step === 1 && "Enter your email to receive an OTP."}
                        {step === 2 && "Enter the OTP sent to your registered email."}
                        {step === 3 && "Create a new password to secure your account."}
                    </Typography>

                    {step === 1 && renderField("Enter email address", email, setEmail)}
                    {step === 2 && renderField("Enter OTP", otpInput, setOtpInput)}
                    {step === 3 && (
                        <>
                            {renderField("Email address", email, setEmail)}
                            {renderField("New password", passwords.new, (v) => setPasswords(p => ({ ...p, new: v })), showPass.new ? 'text' : 'password', 'new')}
                            {renderField("Confirm password", passwords.confirm, (v) => setPasswords(p => ({ ...p, confirm: v })), showPass.confirm ? 'text' : 'password', 'confirm')}
                        </>
                    )}

                    {error && <Typography color="error" sx={{ mb: 2, fontSize: '0.85rem', bgcolor: 'rgba(255,0,0,0.1)', p: 1, borderRadius: 1 }}>{error}</Typography>}

                    <Button
                        fullWidth variant="contained"
                        onClick={step === 1 ? handleGenerateOtp : step === 2 ? handleVerifyOTP : handleChangePassword}
                        sx={{
                            background: 'linear-gradient(90deg, #2563EB 0%, #06B6D4 100%)',
                            border: '1px solid #265BE480',
                            color: '#fff',
                            p: 1.5,
                            fontWeight: 'bold',
                            '&:hover': { background: 'linear-gradient(90deg, #1d4fc4, #0596ab)' }
                        }}
                    >
                        {step === 1 ? "Generate OTP" : step === 2 ? "Verify OTP" : "Change Password"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ForgotPasswordPage;